import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import { putUserEinvoicingRoutine } from '../redux/Users/routines';

const SNOOZE_DAYS = 7;

export default function SiretPrompt() {
  const dispatch = useDispatch();

  const userId = useSelector((state) => state.auth.user);
  const user = useSelector((state) => state.entities && state.entities.users && state.entities.users[userId]);
  // Le SIRET vit dans usersdata (infos).
  const infos = useSelector((state) =>
    user && user.infos && state.entities && state.entities.infos ? state.entities.infos[user.infos] : null
  );

  const [step, setStep] = useState('country'); // 'country' | 'siret'
  const [siret, setSiret] = useState('');
  const [looking, setLooking] = useState(false);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [closedLocal, setClosedLocal] = useState(false);

  // Conditions d'affichage : praticien connecté et complété, sans SIRET,
  // n'ayant pas déclaré un autre pays, et snooze expiré.
  const completed = !!(user && user.infos && infos);
  const snoozeOk =
    !infos || !infos.einvoicing_snooze_until ||
    new Date(infos.einvoicing_snooze_until).getTime() < Date.now();
  const shouldShow =
    !!userId && completed && !infos.siret && infos.einvoicing_country !== 'other' && snoozeOk;

  const open = shouldShow && !closedLocal;

  function save(payload) {
    dispatch(putUserEinvoicingRoutine(payload));
    setClosedLocal(true);
  }

  function snooze() {
    save({ snoozeUntil: new Date(Date.now() + SNOOZE_DAYS * 24 * 3600 * 1000).toISOString() });
  }

  async function lookup() {
    const digits = siret.replace(/\D/g, '');
    if (digits.length !== 9 && digits.length !== 14) {
      setError('SIRET invalide : saisissez 14 chiffres (ou 9 pour le SIREN).');
      return;
    }
    const siren = digits.slice(0, 9);
    setLooking(true);
    setError(null);
    setCompany(null);
    try {
      const r = await fetch(
        `https://recherche-entreprises.api.gouv.fr/search?q=${siren}&page=1&per_page=1`
      );
      const j = await r.json();
      const c = j && j.results && j.results[0];
      if (!c) {
        setError("SIREN introuvable dans le registre des entreprises.");
        return;
      }
      const siege = c.siege || {};
      setCompany({
        name: c.nom_complet || c.nom_raison_sociale || '',
        address: [siege.adresse, [siege.code_postal, siege.libelle_commune].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', '),
      });
    } catch (e) {
      setError('Recherche impossible pour le moment.');
    } finally {
      setLooking(false);
    }
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Facturation électronique</DialogTitle>

      {step === 'country' && (
        <>
          <DialogContent>
            <DialogContentText>
              La réforme de la facturation électronique arrive. Pour que vos factures CephaloMax
              soient conformes, nous avons besoin de savoir où vous exercez.
            </DialogContentText>
            <Typography variant="body1" style={{ marginTop: 8, fontWeight: 500 }}>
              Exercez-vous en France ?
            </Typography>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 16px' }}>
            <Button onClick={snooze} color="default">Plus tard</Button>
            <Box>
              <Button onClick={() => save({ country: 'other' })} color="default" style={{ marginRight: 8 }}>
                Non, autre pays
              </Button>
              <Button onClick={() => { setError(null); setStep('siret'); }} variant="contained" color="primary">
                Oui, en France
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'siret' && (
        <>
          <DialogContent>
            <DialogContentText>
              Renseignez le <strong>SIRET</strong> de votre structure, puis vérifiez qu'il correspond
              bien à votre entreprise avant d'enregistrer.
            </DialogContentText>
            <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
              <TextField
                autoFocus
                label="SIRET (14 chiffres)"
                value={siret}
                onChange={(e) => { setSiret(e.target.value); setCompany(null); }}
                inputProps={{ inputMode: 'numeric' }}
                fullWidth
                margin="dense"
              />
              <Button onClick={lookup} disabled={looking} style={{ marginTop: 8 }}>
                {looking ? <CircularProgress size={20} /> : 'Vérifier'}
              </Button>
            </Box>
            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: 8 }}>{error}</Typography>
            )}
            {company && (
              <Paper variant="outlined" style={{ marginTop: 8, padding: 12 }}>
                <Typography variant="body2" color="textPrimary" style={{ fontWeight: 600 }}>
                  {company.name}
                </Typography>
                {company.address && (
                  <Typography variant="body2" color="textSecondary">{company.address}</Typography>
                )}
              </Paper>
            )}
          </DialogContent>
          <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 16px' }}>
            <Button onClick={snooze} color="default">Plus tard</Button>
            <Box>
              <Button onClick={() => setStep('country')} style={{ marginRight: 8 }}>Retour</Button>
              <Button
                onClick={() => save({ siret })}
                variant="contained"
                color="primary"
                disabled={!company}
              >
                Confirmer et enregistrer
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
